/**
 * HOLD hesitation card.
 *
 * Shows one question to someone who read the product page and has not bought,
 * and posts the answer to the same Google Sheet as More options. The reasoning
 * for all of it - why not exit intent, why two trigger conditions, who is
 * excluded and how the sheet columns are borrowed - is in
 * snippets/hold-hesitation.liquid. This file is only the mechanism.
 */
(() => {
  const KEY = 'hold-hesitation-answered';
  const QUIET_DAYS = 7;

  /**
   * localStorage throws in a private window and in embedded browsers with
   * site data blocked. A visitor who cannot be remembered is better served by
   * never seeing the card than by seeing it on every page view, so a failed
   * read is treated as "already asked".
   */
  const askedRecently = () => {
    try {
      const until = Number(window.localStorage.getItem(KEY) || 0);
      return Number.isFinite(until) && Date.now() < until;
    } catch (error) {
      return true;
    }
  };

  const remember = () => {
    try {
      window.localStorage.setItem(KEY, String(Date.now() + QUIET_DAYS * 864e5));
    } catch (error) {
      // Nothing to do. The card is already closing.
    }
  };

  /** Somebody with a cart did not hesitate. */
  const cartIsEmpty = async () => {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      return cart.item_count === 0;
    } catch (error) {
      // If the cart cannot be read, do not guess - stay silent.
      return false;
    }
  };

  const setHidden = (el, hidden) => {
    if (el) el.hidden = hidden;
  };

  const init = (card) => {
    const endpoint = card.dataset.holdHesitationEndpoint;
    if (!endpoint) return;

    const buyButton = document.querySelector('.product-form__submit');
    if (!buyButton) return;

    /**
     * The buy button being disabled is how this page says "you cannot buy
     * this" - outside the US, or the chosen variant being out of stock.
     * Either way the answer to "what is making you hesitate" is already known
     * and is not hesitation, so the card stays away.
     *
     * Read at the moment of showing, never at load. Dawn toggles the disabled
     * attribute in place when the variant changes (product-info.js
     * toggleSubmitButton), so the element reference survives but its state
     * does not - what was true on load is not what is true thirty seconds
     * later. Caching the answer would show the card to someone who has since
     * selected something they cannot buy.
     */
    const canBuy = () => !buyButton.disabled;

    const invite = card.querySelector('[data-hold-hesitation-invite]');
    const form = card.querySelector('[data-hold-hesitation-form]');
    const done = card.querySelector('[data-hold-hesitation-done]');
    const errorBox = card.querySelector('[data-hold-hesitation-error]');

    const close = () => {
      remember();
      card.hidden = true;
    };

    const reveal = () => {
      if (!card.hidden) return;
      card.hidden = false;
      // Let the element paint hidden first so the transition has a start.
      requestAnimationFrame(() => card.classList.add('hold-hesitation--in'));
    };

    /**
     * Dwell is counted only while the tab is actually in front. Without this a
     * page left open in a background tab qualifies on time alone, which is the
     * exact failure the scroll condition exists to avoid.
     */
    let dwell = 0;
    let seenBuyButton = false;
    let buyButtonInView = false;
    let timer = null;

    /**
     * The card is a strip at the foot of the viewport and the buy button is
     * near the foot of the page, so the two collide unless something keeps
     * them apart. Measured on the live page before this guard existed, the
     * card covered 46 of the button's 52 pixels at 390x844 and again at
     * 1280x800 - the two commonest laptop sizes among them. pointer-events
     * does not help: the outer layer ignores clicks, but the card itself does
     * not, and an opaque thing in front of the button is in front of it
     * whether or not it swallows the tap.
     *
     * So two conditions, not one. The button must have been seen, and must
     * not be on screen now. That is also closer to the intent: the question
     * is for somebody who read the page and moved on, not for somebody whose
     * thumb is over Add to cart. Interrupting that is the worst thing this
     * card could do.
     */
    const maybeShow = () => {
      if (!seenBuyButton || buyButtonInView) return;
      if (dwell < Number(card.dataset.holdHesitationDelay || 30)) return;
      if (!canBuy()) return;
      reveal();
    };

    const startClock = () => {
      if (timer) return;
      timer = window.setInterval(() => {
        dwell += 1;
        maybeShow();
      }, 1000);
    };

    const stopClock = () => {
      if (!timer) return;
      window.clearInterval(timer);
      timer = null;
    };

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopClock();
      else startClock();
    });
    if (!document.hidden) startClock();

    /**
     * Kept running rather than disconnected after the first sighting: the
     * button coming back into view has to pull the card away again, or
     * someone who scrolls back up to buy finds the card sitting on top of the
     * button.
     *
     * This retreat applies in every state, including a half-filled form. An
     * earlier version spared an opened card on the grounds that taking a form
     * away mid-answer is worse than the overlap. Measured, that reasoning did
     * not survive: the opened panel is 592px, seventy per cent of a 390x844
     * viewport, and it covered all 52px of the button. Scrolling back to the
     * buy button is somebody deciding to buy, and nothing this card does may
     * stand in front of that.
     *
     * Nothing is lost by retreating. Only the hidden attribute is touched, so
     * the chosen radio and any typed note are exactly where they were when
     * the card comes back. Focus is dropped first so it is not stranded on an
     * element that is no longer rendered.
     */
    const watcher = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        buyButtonInView = entry.isIntersecting;
        if (buyButtonInView) {
          seenBuyButton = true;
          if (!card.hidden) {
            if (card.contains(document.activeElement)) document.activeElement.blur();
            card.hidden = true;
            card.classList.remove('hold-hesitation--in');
          }
          return;
        }
        maybeShow();
      },
      { threshold: 0 }
    );
    watcher.observe(buyButton);

    card.querySelector('[data-hold-hesitation-close]')?.addEventListener('click', close);

    card.querySelector('[data-hold-hesitation-open]')?.addEventListener('click', () => {
      stopClock();
      setHidden(invite, true);
      setHidden(form, false);
      form?.querySelector('input[type="radio"]')?.focus();
    });

    // Esc closes it, like every other dismissible thing on the site.
    card.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') close();
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.sending === 'true') return;

      const data = new FormData(form);
      const reason = (data.get('reason') || '').toString();
      if (!reason) {
        if (errorBox) {
          errorBox.textContent = 'Pick one so the answer means something.';
          errorBox.hidden = false;
        }
        return;
      }

      form.dataset.sending = 'true';
      if (errorBox) errorBox.hidden = true;

      try {
        // Borrowed columns, so Code.gs needs no change: colour marks the
        // source, size carries the answer, consent carries the free text.
        // text/plain keeps this a simple request with no CORS preflight,
        // which an Apps Script web app cannot answer.
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          redirect: 'follow',
          body: JSON.stringify({
            token: card.dataset.holdHesitationToken || '',
            colour: 'hesitation',
            size: reason,
            email: '',
            consent: (data.get('note') || '').toString().trim(),
            website: (data.get('website') || '').toString(),
            page: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!result || result.ok !== true) throw new Error('refused');

        remember();
        setHidden(form, true);
        setHidden(done, false);
        window.setTimeout(() => {
          card.hidden = true;
        }, 2500);
      } catch (error) {
        form.dataset.sending = 'false';
        if (errorBox) {
          errorBox.textContent = 'That did not send. Please try again.';
          errorBox.hidden = false;
        }
      }
    });
  };

  const start = async () => {
    const card = document.querySelector('[data-hold-hesitation]');
    if (!card || askedRecently()) return;
    if (!(await cartIsEmpty())) return;
    init(card);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
